import { isCancel, CancelToken } from 'axios';
import {
  GROUPING_POLLING_STATES,
  GROUPING_POLL_INTERVAL_MS,
  GROUPING_COMMAND_STATUS_TIMEOUT_MS,
  GROUPING_LAST_COMMAND_TIMEOUT_MS,
  createGroupingNotTriggeredError,
  createGroupingTimeoutError,
  extractCommandIdentifier,
  GROUPING_STATUS_TIMEOUT_MESSAGE,
  isCommandFinished,
  isCommandNewerThanSince,
  normalizeCommandIdentifier,
  toApiUtcDate
} from './utils';

const GROUPING_POLLING_CANCELLED_MESSAGE = 'Grouping polling cancelled';

const createCancelledError = () => {
  const error = new Error(GROUPING_POLLING_CANCELLED_MESSAGE);
  error.name = 'GroupingPollingCancelledError';
  error.code = 'GROUPING_CANCELLED';
  return error;
};

const isCancelledError = (error) => (
  error?.code === 'GROUPING_CANCELLED' ||
  isCancel(error)
);

const isCancelledResponse = (response = {}) => response?.status === 'canceled';

const createRunContext = () => ({
  cancelled: false,
  pendingWaits: new Set(),
  requestCancelSources: new Set()
});

const waitFor = (duration, runContext) => {
  return new Promise((resolve, reject) => {
    if (runContext.cancelled) {
      reject(createCancelledError());
      return;
    }

    let timeoutId = null;
    const cancelWait = () => {
      if (timeoutId) clearTimeout(timeoutId);
      runContext.pendingWaits.delete(cancelWait);
      reject(createCancelledError());
    };

    timeoutId = setTimeout(() => {
      runContext.pendingWaits.delete(cancelWait);
      resolve();
    }, duration);

    runContext.pendingWaits.add(cancelWait);
  });
};

const cancelRunContext = (runContext) => {
  if (!runContext || runContext.cancelled) return;

  runContext.cancelled = true;
  runContext.requestCancelSources.forEach((source) => {
    source.cancel(GROUPING_POLLING_CANCELLED_MESSAGE);
  });
  runContext.requestCancelSources.clear();

  runContext.pendingWaits.forEach((cancelWait) => cancelWait());
  runContext.pendingWaits.clear();
};

class GroupingPollingManager {
  constructor ({
    getLastGroupingCommand,
    getCommandStatus
  }) {
    this.getLastGroupingCommand = getLastGroupingCommand;
    this.getCommandStatus = getCommandStatus;
    this.currentRunContext = null;
    this.state = GROUPING_POLLING_STATES.IDLE;
  }

  cancel () {
    cancelRunContext(this.currentRunContext);
    this.currentRunContext = null;
    this.state = GROUPING_POLLING_STATES.IDLE;
  }

  getState () {
    return this.state;
  }

  async start ({
    workspaceName,
    sinceTimestamp,
    onStateChange,
    onCommandPoll,
    onCommandResolved,
    onFinished,
    onError
  }) {
    this.cancel();

    const runContext = createRunContext();
    this.currentRunContext = runContext;
    const since = toApiUtcDate(new Date(sinceTimestamp));

    try {
      this.state = GROUPING_POLLING_STATES.POLLING;
      if (onStateChange) {
        onStateChange({
          status: GROUPING_POLLING_STATES.POLLING,
          phase: 'lastGrouping',
          since,
          commandId: null,
          error: null
        });
      }

      const commandId = await this.pollLastGroupingCommand({
        workspaceName,
        since,
        sinceTimestamp,
        runContext
      });

      if (runContext.cancelled) throw createCancelledError();

      if (onCommandResolved) {
        onCommandResolved({
          commandId,
          since
        });
      }

      if (onStateChange) {
        onStateChange({
          status: GROUPING_POLLING_STATES.POLLING,
          phase: 'command',
          since,
          commandId,
          error: null
        });
      }

      const commandStatus = await this.pollCommandStatus({
        commandId,
        onPoll: onCommandPoll,
        runContext
      });

      if (runContext.cancelled) throw createCancelledError();

      this.state = GROUPING_POLLING_STATES.FINISHED;
      if (onFinished) {
        onFinished({
          commandId,
          since,
          commandStatus
        });
      }

      if (onStateChange) {
        onStateChange({
          status: GROUPING_POLLING_STATES.FINISHED,
          phase: 'command',
          since,
          commandId,
          error: null
        });
      }

      return {
        commandId,
        since,
        commandStatus
      };
    } catch (error) {
      if (isCancelledError(error)) return null;

      this.state = GROUPING_POLLING_STATES.ERROR;
      if (onError) {
        onError(error);
      }
      if (onStateChange) {
        onStateChange({
          status: GROUPING_POLLING_STATES.ERROR,
          phase: 'error',
          since,
          commandId: null,
          error
        });
      }
      throw error;
    } finally {
      if (this.currentRunContext === runContext) {
        this.currentRunContext = null;
      }
    }
  }

  async pollLastGroupingCommand ({
    workspaceName,
    since,
    sinceTimestamp,
    runContext
  }) {
    const timeoutAt = sinceTimestamp + GROUPING_LAST_COMMAND_TIMEOUT_MS;

    while (!runContext.cancelled) {
      const cancelSource = CancelToken.source();
      runContext.requestCancelSources.add(cancelSource);

      try {
        const response = await this.getLastGroupingCommand(workspaceName, since, cancelSource.token);
        if (isCancelledResponse(response)) throw createCancelledError();

        const commandId = normalizeCommandIdentifier(extractCommandIdentifier(response));
        const isNewerThanSince = isCommandNewerThanSince(response, sinceTimestamp);

        if (commandId && isNewerThanSince) {
          return commandId;
        }
      } catch (error) {
        if (isCancelledError(error)) throw error;
      } finally {
        runContext.requestCancelSources.delete(cancelSource);
      }

      if (Date.now() >= timeoutAt) {
        throw createGroupingNotTriggeredError();
      }

      await waitFor(GROUPING_POLL_INTERVAL_MS, runContext);
    }

    throw createCancelledError();
  }

  async pollCommandStatus ({
    commandId,
    onPoll,
    runContext
  }) {
    const timeoutAt = Date.now() + GROUPING_COMMAND_STATUS_TIMEOUT_MS;
    let pollCount = 0;

    while (!runContext.cancelled) {
      const cancelSource = CancelToken.source();
      runContext.requestCancelSources.add(cancelSource);

      try {
        const response = await this.getCommandStatus(commandId, cancelSource.token);
        if (isCancelledResponse(response)) throw createCancelledError();
        pollCount += 1;

        if (onPoll) {
          onPoll({
            pollCount,
            commandId,
            response
          });
        }

        if (isCommandFinished(response)) {
          return response;
        }
      } finally {
        runContext.requestCancelSources.delete(cancelSource);
      }

      if (Date.now() >= timeoutAt) {
        throw createGroupingTimeoutError(GROUPING_STATUS_TIMEOUT_MESSAGE);
      }

      await waitFor(GROUPING_POLL_INTERVAL_MS, runContext);
    }

    throw createCancelledError();
  }
}

export default GroupingPollingManager;
